import type { ThunkDispatch } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  CL_APPROVED,
  CL_REJECTED,
  CL_REQUESTED,
  ONBOARDED,
  ONBOARDING,
  TP_ADDED,
  API_REQUEST_TIMEOUT_MS,
  VITE_API_V2_URL,
} from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import type { TermsheetStatusCounts } from "helpers/termsheetStatusCounts";
import {
  transformAssociateDocumentUploadError,
  type AssociateDocumentUploadError,
} from "helpers/documentUploadApiHelpers";
import { setAppState } from "redux/reducers/appStatesReducer";
import type {
  Account,
  AccountRelationship,
  AddDocumentToBillingInvoice,
  AssociateCompanyDocs,
  Company,
  CompanyDoc,
  CountdownInfo,
  CountdownUpdateRequest,
  CountryEligibility,
  CreditLimit,
  ILead,
  InviteTradePartners,
  ISFLead,
  PartnerData,
  RegisterCompany,
  RequiredDoc,
  SfExportReadiness,
  Step,
  Termsheet,
  TermsheetApprover,
  TermsheetComment,
  TermsheetInfo,
  TermsheetInvoice,
  TermSheetListItem,
  TermsheetWithPartner,
  UpdateStep,
} from "types";
import type {
  AccountList,
  AccountOnboardingStatus,
  AddDirectorData,
  AddDocumentToContactData,
  AddDocumentToDirectorData,
  AddShareholderData,
  CustomerRejectRequest,
  EditDirectorData,
  EditShareholderData,
  InternalApprovalTermSheetSubmitRequest,
  InternalApproveRequest,
  InternalRejectRequest,
  TermsheetReassignRequest,
  TermsheetRecallRequest,
  UpdatePartnerTermsheet,
  UpdateRelationshipSchema,
} from "./api-accounts.d";
import { companiesrtk } from "./api-company";
import { compliance } from "./api-compliance";
import { maps } from "./api-maps";
import { misc } from "./api-misc";
import { users_v2 } from "./api-users-v2";

const toCompanyDoc = (payload: unknown): CompanyDoc | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const raw =
    "data" in payload && (payload as { data?: unknown }).data != null
      ? (payload as { data: unknown }).data
      : payload;

  if (!raw || typeof raw !== "object") {
    return null;
  }

  const doc = raw as Record<string, unknown>;
  if (typeof doc.id !== "string") {
    return null;
  }

  return {
    id: doc.id,
    name: String(doc.name ?? ""),
    document_type: String(doc.required_document_id ?? doc.document_type ?? ""),
    document_type_name:
      typeof doc.document_type_name === "string"
        ? doc.document_type_name
        : undefined,
    url: typeof doc.url === "string" ? doc.url : undefined,
    year:
      doc.year === null || doc.year === undefined ? doc.year : Number(doc.year),
    submitted_at:
      typeof doc.submitted_at === "string" ? doc.submitted_at : undefined,
    submitted_to_sf:
      typeof doc.submitted_to_sf === "boolean"
        ? doc.submitted_to_sf
        : undefined,
  };
};

const createOptimisticCompanyDoc = (
  file: File,
  documentType: string,
  year?: string | null,
): CompanyDoc => ({
  id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: file.name,
  document_type: documentType,
  year: year ? Number(year) : null,
});

const mergeUploadedDocument = (
  draft: CompanyDoc[],
  optimisticId: string,
  response: unknown,
): void => {
  const created = toCompanyDoc(response);
  const optimisticIndex = draft.findIndex((doc) => doc.id === optimisticId);

  if (created) {
    if (optimisticIndex >= 0) {
      draft[optimisticIndex] = created;
      return;
    }

    if (!draft.some((doc) => doc.id === created.id)) {
      draft.push(created);
    }

    return;
  }

  if (optimisticIndex >= 0) {
    draft.splice(optimisticIndex, 1);
  }
};

const incrementDocCount = (
  doc: RequiredDoc,
  documentTypeId: string,
): boolean => {
  if (doc.id === documentTypeId) {
    doc.count_unique_uploaded = (doc.count_unique_uploaded ?? 0) + 1;
    return true;
  }

  if (!doc.items?.length) {
    return false;
  }

  let matched = false;
  doc.items = doc.items.map((sub) => {
    if (sub.id === documentTypeId) {
      matched = true;
      return {
        ...sub,
        count_unique_uploaded: (sub.count_unique_uploaded ?? 0) + 1,
      };
    }
    return sub;
  });
  return matched;
};

const decrementDocCount = (
  doc: RequiredDoc,
  documentTypeId: string,
): boolean => {
  if (doc.id === documentTypeId) {
    doc.count_unique_uploaded = Math.max(
      0,
      (doc.count_unique_uploaded ?? 0) - 1,
    );
    return true;
  }

  if (!doc.items?.length) {
    return false;
  }

  let matched = false;
  doc.items = doc.items.map((sub) => {
    if (sub.id === documentTypeId) {
      matched = true;
      return {
        ...sub,
        count_unique_uploaded: Math.max(
          0,
          (sub.count_unique_uploaded ?? 0) - 1,
        ),
      };
    }
    return sub;
  });
  return matched;
};

const patchChecklistAfterUpload = (
  draft: { complete?: boolean; data: RequiredDoc[] },
  documentTypeId: string,
): void => {
  draft.data?.forEach((doc) => {
    incrementDocCount(doc, documentTypeId);
  });
};

const patchChecklistAfterDelete = (
  draft: { complete?: boolean; data: RequiredDoc[] },
  documentTypeId: string,
): void => {
  draft.data?.forEach((doc) => {
    decrementDocCount(doc, documentTypeId);
  });
};

const resolveUploadAccountId = (id: string, data: AssociateCompanyDocs) =>
  data.partner_id !== undefined ? data.partner_id : id;

const getAmount = (credit_limit: CreditLimit, status: string): number => {
  switch (status) {
    case CL_REJECTED:
      return credit_limit?.requested_amount;
    case CL_REQUESTED:
      return credit_limit?.requested_amount;
    case CL_APPROVED:
      return credit_limit?.approved_amount;
    default:
      return credit_limit.requested_amount ?? 0;
  }
};

const formatBuyers = (
  buyers: {
    country: string;
    credit_limit: CreditLimit;
    id: string;
    name: string;
    profile: string;
    reg_no: string;
    is_termsheet_signed: boolean;
    onboarding_submitted: boolean;
    internal_name: string;
    termsheet_signed_date: Date;
    product: string;
    is_lead_complete: boolean;
    average_invoice_amount: number;
    average_payment_terms: string;
    number_of_past_paid_transaction: number;
    industry: string;
    goods_category: string;
    annual_revenue: number;
    termsheet_status: string;
    country_eligiblilty?: CountryEligibility;
    country_matrix_blocked?: boolean;
    // document_reviewed?: boolean;
    document_reviewed_risk?: boolean;
    document_reviewed_operation?: boolean;
    call_status?: boolean | null;
  }[],
): PartnerData[] => {
  return buyers.map((item) => {
    return {
      id: item.id,
      name: item.name,
      termsheetSigned: item.is_termsheet_signed ?? false,
      internal_name: item.internal_name,
      termsheetSignedDate: item.termsheet_signed_date,
      product: item.product,
      reg_no: item.reg_no,
      country: item.country,
      documents_completed: true,
      //eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      clAmount: item?.credit_limit
        ? //eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          getAmount(item?.credit_limit, item.credit_limit?.status)
        : 0,
      approval: item.credit_limit?.status,
      profile: item.profile,
      credit_limit: item.credit_limit,
      onboardingSubmitted: item.onboarding_submitted,
      is_lead_complete: item.is_lead_complete,
      average_invoice_amount: item.average_invoice_amount,
      average_payment_terms: item.average_payment_terms,
      number_of_past_paid_transaction: item.number_of_past_paid_transaction,
      industry: item.industry,
      goods_category: item.goods_category,
      annual_revenue: item.annual_revenue,
      termsheet_status: item.termsheet_status,
      country_eligiblilty: item.country_eligiblilty,
      country_matrix_blocked: item.country_matrix_blocked,
      // document_reviewed: item.document_reviewed,
      document_reviewed_risk: item.document_reviewed_risk,
      document_reviewed_operation: item.document_reviewed_operation,
      call_status: item.call_status,
    };
  });
};

const formatLeads = (
  data: {
    country: string;
    id: string;
    name: string;
    sales_owner: string;
    source: string;
    created_at: Date;
    profile: string;
    internal_name: string;
  }[],
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  },
): any => {
  // You can use pagination data if needed
  return data?.map((item) => ({
    internal_name: item.internal_name,
    id: item.id,
    name: item.name,
    profile: item.profile,
    sales_owner: item.sales_owner,
    source: item.source,
    country: item.country,
    created_at: item.created_at,
  }));
};

const formatAccountsResponse = (data: AccountList[]): AccountList[] => {
  return data.map((item) => ({
    id: item.id,
    internal_name: item.internal_name,
    name: item.name,
    status: item.status,
    profile: item.profile,
    reg_no: item.reg_no,
    country: item.country,
    users: item.users,
    created_at: item.created_at,
  }));
};

const updateTermsheet = (
  partner_id: string,
  lead_id: string,
  newTermsheet: Termsheet,
  dispatch: ThunkDispatch<any, any, any>,
  reduxSlice: any,
) => {
  // eslint-disable-next-line no-use-before-define
  dispatch(
    reduxSlice.util.upsertQueryData(
      "getLeadPartnerTermsheet",
      { partner_id, lead_id },
      newTermsheet,
    ),
  );
  // eslint-disable-next-line no-use-before-define
  dispatch(
    reduxSlice.util.updateQueryData(
      "getTermsheetByInternalName",
      newTermsheet.internal_name,
      (draft: any) => Object.assign(draft, newTermsheet),
    ),
  );
  // eslint-disable-next-line no-use-before-define
  dispatch(
    reduxSlice.util.updateQueryData(
      "getTermsheetById",
      newTermsheet.id,
      (draft: any) => Object.assign(draft, newTermsheet),
    ),
  );
};

const ACCOUNTS_V2 = "/accounts";
const ACCOUNT_TAGS = {
  COMPANY: "Company",
  BUYERS: "Buyers",
  RELATIONSHIP: "Relationship",
  ACCOUNT_DOCS: "ACCOUNT_DOCS",
  ACCOUNT_DOC_CHECKLIST: "ACCOUNT_DOC_CHECKLIST",
  ACCOUNT_DOC_UPLOADS: "ACCOUNT_DOC_UPLOADS",
  USER_DOCS: "USER_DOCS",
  LEAD: "LEAD",
  ACCOUNTS: "ACCOUNTS",
  ACCOUNT: "ACCOUNT",
  PARTNERS: "Partners",
  BILLING_DOCS: "BILLING_DOCS",
  ACCOUNT_CREDIT_LIMIT: "ACCOUNT_CREDIT_LIMIT",
  PARTNER_CONTACTS: "PARTNER_CONTACTS",
  SHAREHOLDERS: "SHAREHOLDERS",
  ID_VERIFICATION: "IDVerification",
  ACCOUNT_ONBOARDING_STATUS: "ACCOUNT_ONBOARDING_STATUS",
  ADDRESS_PROOF: "ADDRESSPROOF",
  TERMSHEETS: "TERMSHEETS",
  TERMSHEET_COMMENTS: "TERMSHEET_COMMENTS",
} as const;

const ACCOUNT_QUERY_KEYS = {
  GET_ACCOUNT: "getAccount",
  GET_ACCOUNT_BY_NAME: "getAccountByName",
  GET_LEAD_BY_NAME: "getLeadByName",
} as const;

const ACCOUNT_TYPE_LEAD = "LEAD";
export const accountrtk = createApi({
  reducerPath: "accountrtk",
  tagTypes: [
    ACCOUNT_TAGS.COMPANY,
    ACCOUNT_TAGS.BUYERS,
    ACCOUNT_TAGS.RELATIONSHIP,
    ACCOUNT_TAGS.ACCOUNT_DOCS,
    ACCOUNT_TAGS.ACCOUNT_DOC_CHECKLIST,
    ACCOUNT_TAGS.ACCOUNT_DOC_UPLOADS,
    ACCOUNT_TAGS.USER_DOCS,
    ACCOUNT_TAGS.LEAD,
    ACCOUNT_TAGS.ACCOUNTS,
    ACCOUNT_TAGS.ACCOUNT,
    ACCOUNT_TAGS.PARTNERS,
    ACCOUNT_TAGS.BILLING_DOCS,
    ACCOUNT_TAGS.ACCOUNT_CREDIT_LIMIT,
    ACCOUNT_TAGS.PARTNER_CONTACTS,
    ACCOUNT_TAGS.SHAREHOLDERS,
    ACCOUNT_TAGS.ID_VERIFICATION,
    ACCOUNT_TAGS.ACCOUNT_ONBOARDING_STATUS,
    ACCOUNT_TAGS.ADDRESS_PROOF,
    ACCOUNT_TAGS.TERMSHEETS,
    ACCOUNT_TAGS.TERMSHEET_COMMENTS,
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: VITE_API_V2_URL,
  }),
  endpoints: (builder) => ({
    createAccount: builder.mutation<Account, RegisterCompany>({
      query: (payload) => {
        const { client = false, ...data } = payload;
        const isClient = Boolean(client);
        return {
          url: `${ACCOUNTS_V2}${isClient ? "?mode=client" : ""}`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: [ACCOUNT_TAGS.ACCOUNTS, ACCOUNT_TAGS.COMPANY],
      transformResponse: (response) => response?.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const isClient = Boolean(arg.client);
          if (isClient) {
            dispatch(users_v2.util.invalidateTags(["Users", "CompanyUsers"]));
          }
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    updateAccount: builder.mutation<
      Account,
      Partial<Account> & { type?: string }
    >({
      query(payload) {
        //eslint-disable-next-line unused-imports/no-unused-vars
        const { id, type, ...data } = payload;
        return {
          url: `${ACCOUNTS_V2}/${id}`,
          method: "PUT",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: [ACCOUNT_TAGS.ACCOUNT_ONBOARDING_STATUS],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: newPartner } = await queryFulfilled;
          dispatch(
            accountrtk.util.upsertQueryData(
              ACCOUNT_QUERY_KEYS.GET_ACCOUNT,
              newPartner.id,
              newPartner,
            ),
          );
          dispatch(
            accountrtk.util.upsertQueryData(
              ACCOUNT_QUERY_KEYS.GET_ACCOUNT_BY_NAME,
              newPartner.internal_name,
              newPartner,
            ),
          );
          dispatch(
            accountrtk.util.upsertQueryData(
              ACCOUNT_QUERY_KEYS.GET_LEAD_BY_NAME,
              newPartner.internal_name,
              newPartner,
            ),
          );
          if (arg.type === ACCOUNT_TYPE_LEAD) {
            dispatch(accountrtk.util.invalidateTags([ACCOUNT_TAGS.LEAD]));
          }
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
          dispatch(
            accountrtk.util.invalidateTags([
              ACCOUNT_TAGS.ACCOUNTS,
              ACCOUNT_TAGS.ACCOUNT,
              ACCOUNT_TAGS.PARTNERS,
            ]),
          );
        }
      },
    }),
    submitAccount: builder.mutation<
      string,
      {
        accountId: string;
        data: {
          is_submitted?: boolean;
          agree_to_terms?: boolean;
          agree_to_privacy_policy?: boolean;
          partner_id?: string;
        };
      }
    >({
      query: (payload) => {
        const { accountId, data } = payload;
        return {
          url: `${ACCOUNTS_V2}/${accountId}/submit-onboarding`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: ["ACCOUNTS", "ACCOUNT", "Relationship"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const isPartner = Boolean(arg?.data?.partner_id);
          if (!isPartner)
            dispatch(companiesrtk.util.invalidateTags(["Company"]));
          if (isPartner) dispatch(accountrtk.util.invalidateTags(["Partners"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    getAccounts: builder.query<any, { accountparams: any }>({
      query: ({ accountparams }) => ({
        url: `${ACCOUNTS_V2}${accountparams}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["ACCOUNTS"],
    }),

    getAccountByName: builder.query<Account, string>({
      query: (internal_name) => ({
        url: `${ACCOUNTS_V2}/name/${internal_name}`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
      providesTags: ["ACCOUNT"],
    }),
    getAccountRelationship: builder.query<
      AccountRelationship,
      { companyId: string; partnerId: string; map_id?: string }
    >({
      query: ({ companyId, partnerId, map_id }) => ({
        url: `${ACCOUNTS_V2}/${companyId}/partners/${partnerId}/relationships`,
        method: 'get',
        params: map_id ? { map_id } : undefined,
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["Relationship"],
    }),
    modifyAccountRelationship: builder.mutation<
      any,
      {
        companyId: string;
        partnerId: string;
        data: UpdateRelationshipSchema;
      }
    >({
      query: ({ companyId, partnerId, data }) => {
        return {
          url: `${ACCOUNTS_V2}/${companyId}/partners/${partnerId}/relationships`,
          method: "put",
          data,
        };
      },
      transformResponse: (response) => response?.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: newRelationship } = await queryFulfilled;
          dispatch(
            accountrtk.util.upsertQueryData(
              "getAccountRelationship",
              { companyId: arg.companyId, partnerId: arg.partnerId },
              newRelationship,
            ),
          );
        } catch (e) {
          dispatch(accountrtk.util.invalidateTags(["Relationship"]));
        }
      },
    }),
    patchAccountRelationship: builder.mutation<
      any,
      {
        companyId: string;
        partnerId: string;
        data: Partial<UpdateRelationshipSchema>;
      }
    >({
      query: ({ companyId, partnerId, data }) => {
        return {
          url: `${ACCOUNTS_V2}/${companyId}/partners/${partnerId}/relationships`,
          method: "patch",
          data,
        };
      },
      invalidatesTags: ["Relationship"],
    }),
    getPartnerContacts: builder.query<
      any,
      { companyId: string; partnerId: string }
    >({
      query: ({
        companyId,
        partnerId,
      }: {
        companyId: string;
        partnerId: string;
      }) => ({
        url: `${ACCOUNTS_V2}/${companyId}/partners/${partnerId}/contacts`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["PARTNER_CONTACTS"],
    }),
    updateSubmitStatus: builder.mutation<
      void,
      {
        companyId: string;
        partnerId: string;
        data: {
          is_submitted?: boolean;
          agree_to_terms?: boolean;
          agree_to_privacy_policy?: boolean;
        };
      }
    >({
      query(payload) {
        const { companyId, partnerId, data } = payload;
        return {
          url: `/companies/${companyId}/relationships/${partnerId}/submit-status`,
          method: "PUT",
          data,
        };
      },
      // Invalidates all queries that subscribe to this Post `id` only.
      // In this case, `getUser` will be re-run. `getCompanyUsers` *might*  rerun, if this id was under its results.
      invalidatesTags: () => ["Relationship"],
    }),
    getAccount: builder.query<Account, string>({
      query: (id: string) => ({
        url: `${ACCOUNTS_V2}/${id}`,
        method: "get",
        timeout: API_REQUEST_TIMEOUT_MS,
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result) => [
        "ACCOUNT",
        { type: "ACCOUNT", id: result?.id },
      ],
    }),

    getBuyers: builder.query<PartnerData[], string>({
      query: (id: string) => ({
        url: `/buyers${id !== "" ? `?company_id=${id}` : ""}`,
        method: "get",
        timeout: API_REQUEST_TIMEOUT_MS,
      }),
      transformResponse: (response) => formatBuyers(response?.data),
      providesTags: ["Buyers"],
    }),
    getPartners: builder.query<
      PartnerData[],
      string | { id: string; queryParams?: Record<string, string> }
    >({
      query: (arg) => {
        const id = typeof arg === "string" ? arg : arg.id;
        const queryParams =
          typeof arg === "object" && arg.queryParams ? arg.queryParams : {};

        const queryString =
          Object.keys(queryParams).length > 0
            ? `?${new URLSearchParams(queryParams).toString()}`
            : "";

        return {
          url: `${ACCOUNTS_V2}/${id}/partners${queryString}`,
          method: "get",
          timeout: API_REQUEST_TIMEOUT_MS,
        };
      },
      transformResponse: (response) => formatBuyers(response?.data),
      providesTags: ["Partners"],
    }),

    getBuyerCompanies: builder.query<Company[], string>({
      query: (id: string) => ({
        url: `/buyers?company_id=${id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["Buyers"],
    }),
    inviteTradePartners: builder.mutation<void, InviteTradePartners>({
      query: (data) => {
        const { company_id, ...payload } = data;
        return {
          url: `${ACCOUNTS_V2}/${company_id}/partners`,
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: [
        "Buyers",
        "Company",
        "Partners",
        "ACCOUNT_CREDIT_LIMIT",
      ],
      async onQueryStarted(_id, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const currentState = (getState() as { appState: { value: string } })
            .appState.value;
          const isPostSales = [ONBOARDING, ONBOARDED].includes(currentState);
          if (!isPostSales) {
            dispatch(setAppState(TP_ADDED));
          }
          dispatch(companiesrtk.util.invalidateTags(["Buyers"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    updateTermSheetStatus: builder.mutation<
      void,
      {
        account_id: string;
        partner_id: string;
      }
    >({
      query: (data) => {
        const { account_id, partner_id } = data;
        return {
          url: `${ACCOUNTS_V2}/${account_id}/partners/${partner_id}/update-termsheet-status`,
          method: "POST",
        };
      },
      invalidatesTags: ["Partners", "Relationship"],
      async onQueryStarted(_id, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const currentState = (getState() as { appState: { value: string } })
            .appState.value;
          const isPostSales = [ONBOARDING, ONBOARDED].includes(currentState);
          if (!isPostSales) {
            dispatch(setAppState(TP_ADDED));
          }
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),

    getAccountingSoftwares: builder.query<string[], void>({
      query: () => ({
        url: `/accounting-softwares`,
        method: "get",
      }),
      transformResponse: (response) =>
        response?.data?.map((industry: string) => {
          return { name: industry };
        }),
    }),
    getGoods: builder.query<string[], string>({
      query: (industry: string) => ({
        url: `/goods/${industry}`,
        method: "get",
      }),
      transformResponse: (response) =>
        response?.data?.map((good: string) => {
          return { name: good };
        }),
    }),
    getAccountSoftware: builder.query<string[], null>({
      query: () => ({
        url: `/accounting-softwares`,
        method: "get",
      }),
      transformResponse: (response) =>
        response?.data?.map((accountingSoftware: string) => {
          return { name: accountingSoftware };
        }),
    }),
    vendorAddress: builder.mutation<
      void,
      {
        iso3: string;
        registrationNumber: string;
        city: string;
        postcode: string;
        address_line1: string;
        address_line2: string;
      }
    >({
      query: (data) => {
        return {
          url: `/vendor-address`,
          method: "POST",
          data,
        };
      },
    }),
    //DOCS
    getDocuments: builder.query<
      {
        complete: boolean;
        data: RequiredDoc[];
      },
      {
        account_id: string;
      }
    >({
      query: ({ account_id }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/documents/list`,
        method: "get",
      }),
      providesTags: (result, error, { account_id }) => [
        "ACCOUNT_DOCS",
        { type: "ACCOUNT_DOC_CHECKLIST", id: account_id },
      ],
      transformResponse: (response: {
        data?: RequiredDoc[];
        complete?: boolean;
      }) => ({
        // API shape: { data: RequiredDoc[], complete: boolean }
        complete: Boolean(response?.complete),
        data: Array.isArray(response?.data) ? response.data : [],
      }),
    }),
    getDocumentUrl: builder.query<
      {
        id: string;
        name: string;
        url: string;
        document_type: string;
      },
      { account_id: string; document_id: string }
    >({
      query: ({
        account_id,
        document_id,
      }: {
        account_id: string;
        document_id: string;
      }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/documents/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
    }),
    getDocumentContent: builder.query<
      string,
      { account_id: string; document_id: string }
    >({
      query: ({
        account_id,
        document_id,
      }: {
        account_id: string;
        document_id: string;
      }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/documents/${document_id}/download`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
    }),
    getAccountDocuments: builder.query<CompanyDoc[], string>({
      query: (accountId) => {
        return {
          url: `${ACCOUNTS_V2}/${accountId}/documents`,
          method: "get",
          timeout: API_REQUEST_TIMEOUT_MS,
        };
      },
      transformResponse: (response) => response.data,
      providesTags: (result, error, accountId) => [
        "ACCOUNT_DOCS",
        { type: "ACCOUNT_DOC_UPLOADS", id: accountId },
      ],
    }),
    getUserDocs: builder.query<RequiredDoc[], string>({
      query: (country) => {
        return {
          url: `/required-documents?type=user&country=${country}`,
          method: "get",
        };
      },
      providesTags: ["USER_DOCS"],
    }),
    associateDocs: builder.mutation<
      any,
      { id: string; data: AssociateCompanyDocs }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_category", data.document_category);
        formData.append("document_type", data.document_type);
        //eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (data.year) formData.append("year", data.year);
        if (data.upload_context) {
          formData.append("upload_context", data.upload_context);
        }
        if (data.base_document_type) {
          formData.append("base_document_type", data.base_document_type);
        }
        if (data.document_label) {
          formData.append("document_label", data.document_label);
        }
        if (data.note) {
          formData.append("note", data.note);
        }
        const url = `${ACCOUNTS_V2}/${data.partner_id !== undefined ? data.partner_id : id}/documents`;

        return {
          url,
          method: "POST",
          data: formData,
        };
      },
      transformErrorResponse: (error, _meta, { data }) =>
        transformAssociateDocumentUploadError(
          error as AssociateDocumentUploadError,
          data.document_type,
        ),
      invalidatesTags: (result, error, { id, data }) => {
        if (error) {
          return [];
        }
        const accountId = resolveUploadAccountId(id, data);
        return [
          { type: "ACCOUNT_ONBOARDING_STATUS", id: accountId },
          { type: "ACCOUNT_DOC_UPLOADS", id: accountId },
          "ACCOUNT_DOCS",
        ];
      },
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        const accountId = resolveUploadAccountId(id, data);
        const optimisticDoc = createOptimisticCompanyDoc(
          data.file,
          data.document_type,
          data.year,
        );
        const patchUploads = dispatch(
          accountrtk.util.updateQueryData(
            "getAccountDocuments",
            accountId,
            (draft) => {
              draft.push(optimisticDoc);
            },
          ),
        );
        const patchChecklist = dispatch(
          accountrtk.util.updateQueryData(
            "getDocuments",
            { account_id: accountId },
            (draft) => {
              patchChecklistAfterUpload(draft, data.document_type);
            },
          ),
        );
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            accountrtk.util.updateQueryData(
              "getAccountDocuments",
              accountId,
              (draft) => {
                mergeUploadedDocument(draft, optimisticDoc.id, response);
              },
            ),
          );
          dispatch(companiesrtk.util.invalidateTags(["Company"]));
        } catch {
          patchUploads.undo();
          patchChecklist.undo();
        }
      },
    }),
    updateSteps: builder.mutation<Step, UpdateStep>({
      query(payload) {
        const { company_id, ...data } = payload;
        return {
          url: `${ACCOUNTS_V2}/${company_id}/update-steps`,
          method: "POST",
          data,
        };
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(misc.util.invalidateTags(["STEPS"]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    deleteDoc: builder.mutation<any, { account_id: string; id: string }>({
      query: ({ account_id, id }) => {
        return {
          url: `${ACCOUNTS_V2}/${account_id}/documents/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, { account_id }) => {
        if (error) {
          return [];
        }
        return [
          { type: "ACCOUNT_ONBOARDING_STATUS", id: account_id },
          "USER_DOCS",
          "IDVerification",
          "ADDRESSPROOF",
        ];
      },
      async onQueryStarted({ account_id, id }, { dispatch, queryFulfilled }) {
        let deletedDocumentType: string | undefined;
        const patchUploads = dispatch(
          accountrtk.util.updateQueryData(
            "getAccountDocuments",
            account_id,
            (draft) => {
              const index = draft.findIndex((doc) => doc.id === id);
              if (index >= 0) {
                deletedDocumentType = draft[index]?.document_type;
                draft.splice(index, 1);
              }
            },
          ),
        );
        const patchChecklist =
          deletedDocumentType !== undefined
            ? dispatch(
                accountrtk.util.updateQueryData(
                  "getDocuments",
                  { account_id },
                  (draft) => {
                    patchChecklistAfterDelete(draft, deletedDocumentType!);
                  },
                ),
              )
            : null;
        try {
          await queryFulfilled;
          dispatch(companiesrtk.util.invalidateTags(["Company"]));
          dispatch(companiesrtk.util.invalidateTags(["USER_DOCS"]));
          dispatch(compliance.util.invalidateTags(["SCREENING"]));
          dispatch(maps.util.invalidateTags(["MAP_DOCUMENTS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["DIRECTORS"]));
        } catch (err) {
          patchUploads.undo();
          patchChecklist?.undo();
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    fetchMemberDocuments: builder.query<
      Array<{
        id: string;
        name: string;
        url: string;
        document_type: string;
        document_type_name?: string;
        submitted_at?: string;
        year?: number | null;
      }>,
      { id: string; account_id: string }
    >({
      query: ({ id, account_id }) => {
        const url = `${ACCOUNTS_V2}/${account_id}/contacts/${id}/documents`;
        return {
          url,
          method: "get",
        };
      },
      providesTags: (result, error, { id }) => [
        { type: "USER_DOCS", id },
        "USER_DOCS",
      ],
      transformResponse: (response) => response.data,
    }),
    fetchMemberPendingDocuments: builder.query<
      {
        complete: boolean;
        data: RequiredDoc[];
      },
      { user_id: string; account_id: string }
    >({
      query: ({
        user_id,
        account_id,
      }: {
        user_id: string;
        account_id: string;
      }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/contacts/${user_id}/pending-documents`,
        method: "get",
      }),
    }),
    getLeads: builder.query<any, { leadsparams: any }>({
      query: ({ leadsparams }) => {
        return {
          url: `${ACCOUNTS_V2}/leads${leadsparams}`,
          method: "get",
        };
      },
      providesTags: ["ACCOUNTS"],
      transformResponse: (response) => response?.data,
    }),
    toggleLeadActiveStatus: builder.mutation<
      any,
      { internal_name: string; is_active: boolean }
    >({
      query: ({ internal_name, is_active }) => ({
        url: `${ACCOUNTS_V2}/leads/${internal_name}/toggle-active`,
        method: "PATCH",
        data: { is_active },
      }),
      invalidatesTags: ["ACCOUNTS"],
      transformResponse: (response) => response?.data,
    }),
    getLeadByURI: builder.query<ILead, string>({
      query: (lead_uri) => ({
        url: `${ACCOUNTS_V2}/leads/${lead_uri}`,
        method: "get",
      }),
      providesTags: ["ACCOUNTS"],
      transformResponse: (response) => response.data,
    }),
    addAccountProfile: builder.mutation<
      any,
      { account_id: string; company_type: string }
    >({
      query: ({ account_id, company_type }) => {
        return {
          url: `${ACCOUNTS_V2}/${account_id}/profile`,
          data: { company_type },
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        "Company",
        "ACCOUNT",
        { type: "ACCOUNT", id: arg.account_id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(companiesrtk.util.invalidateTags([ACCOUNT_TAGS.COMPANY]));
          dispatch(accountrtk.util.invalidateTags([ACCOUNT_TAGS.ACCOUNT]));
        } catch (err) {
          //eslint-disable-next-line no-console
          console.log(err);
        }
      },
    }),
    /** When no DB row exists, API returns 200 with { credit_limit: null, credit_rating?, … } (no `id`). Real CL rows always include `id`. */
    getBuyerCreditLimit: builder.query<CreditLimit | null, string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/credit-limit`,
        method: "get",
      }),
      keepUnusedDataFor: 21600,
      providesTags: ["ACCOUNT_CREDIT_LIMIT"],
      transformResponse: (response) => {
        const data = response?.data;
        if (!data) {
          return null;
        }
        if (data?.credit_limit === null || data?.credit_limit === undefined) {
          if (data?.id) {
            return data as CreditLimit;
          }
          return null;
        }
        if (data?.id) {
          return data as CreditLimit;
        }
        return (data.credit_limit ?? null) as CreditLimit | null;
      },
    }),
    getLeadByName: builder.query<Account, string>({
      query: (internal_name) => ({
        url: `${ACCOUNTS_V2}/leads/${internal_name}`,
        method: "get",
      }),
      providesTags: ["LEAD"],
      transformResponse: (response) => response?.data,
    }),
    getLeadSfExportReadiness: builder.query<SfExportReadiness, string>({
      query: (internal_name) => ({
        url: `${ACCOUNTS_V2}/leads/${internal_name}/sf-export-readiness`,
        method: "get",
      }),
      providesTags: ["LEAD"],
      transformResponse: (response) => response?.data,
    }),
    updateTermsheetInfo: builder.mutation<
      TermsheetInfo,
      { accountId: string; body: TermsheetInfo }
    >({
      query: ({ accountId, body }) => ({
        url: `${ACCOUNTS_V2}/${accountId}/termsheetinfo`,
        method: "put",
        data: body,
      }),
      transformResponse: (response) => response?.data,
      async onQueryStarted(arg, api) {
        try {
          const { data } = await api.queryFulfilled;
          api.dispatch(
            accountrtk.util.upsertQueryData(
              "getLeadAccountTermsheetInfo",
              arg.accountId,
              data,
            ),
          );
        } catch (err) {
          api.dispatch(accountrtk.util.invalidateTags(["LEAD"]));
        }
      },
    }),
    getLeadAccountTermsheetInfo: builder.query<TermsheetInfo, string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/termsheetinfo`,
        method: "get",
      }),
      providesTags: ["LEAD"],
      transformResponse: (response) => response?.data,
    }),

    // TERMSHEET
    updateTermsheetCountdown: builder.mutation<
      CountdownInfo,
      { id: string; body: CountdownUpdateRequest }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/termsheet/countdown/${payload.id}`,
        method: "patch",
        data: payload.body,
      }),
      invalidatesTags: ["TERMSHEETS"],
      transformResponse: (response) => response?.data,
    }),

    getTermsheetStatusCounts: builder.query<TermsheetStatusCounts, void>({
      query: () => ({
        url: `${ACCOUNTS_V2}/termsheet/status-counts`,
        method: "get",
      }),
      providesTags: ["TERMSHEETS"],
      transformResponse: (response: { data: TermsheetStatusCounts }) => response.data,
    }),

    getTermsheetList: builder.query<any, { termshetparams: any }>({
      query: ({ termshetparams }) => ({
        url: `${ACCOUNTS_V2}/termsheet/list${termshetparams}`,
        method: "get",
      }),
      providesTags: ["TERMSHEETS"],
      transformResponse: (response) => response?.data,
    }),

    getCustomerTemsheetList: builder.query<
      any,
      { termshetparams: string; accountId: string }
    >({
      query: (arg) => {
        const queryString = arg.termshetparams.startsWith("?")
          ? arg.termshetparams
          : `?${arg.termshetparams}`;

        const accountId = arg.accountId;
        return {
          url: `${ACCOUNTS_V2}/${accountId}/termsheet/customer_termsheet_list${queryString}`,
          method: "get",
        };
      },
      providesTags: ["TERMSHEETS"],
      transformResponse: (response) => response?.data,
    }),
    getDashboardTermsheetList: builder.query<
      TermSheetListItem[],
      { account_id?: string } | null
    >({
      query: (arg) => {
        const account_id =
          arg && typeof arg === "object" && arg.account_id
            ? arg.account_id
            : "";
        const url = account_id
          ? `${ACCOUNTS_V2}/termsheet/dashboard_list?account_id=${account_id}`
          : `${ACCOUNTS_V2}/termsheet/dashboard_list`;
        return {
          url,
          method: "get",
        };
      },
      providesTags: ["TERMSHEETS"],
      transformResponse: (response) => response?.data,
    }),
    getLeadPartnerTermsheet: builder.query<
      Termsheet,
      {
        lead_id: string;
        partner_id: string;
        account_id?: string;
      }
    >({
      query: (val) => {
        const { lead_id, partner_id, account_id } = val;
        let url = `${ACCOUNTS_V2}/${lead_id}/partners/${partner_id}/termsheet`;
        if (account_id) {
          url += `?account_id=${account_id}`;
        }
        return {
          url,
          method: "get",
        };
      },
      providesTags: [ACCOUNT_TAGS.TERMSHEETS, ACCOUNT_TAGS.PARTNERS],
      transformResponse: (response) => response?.data ?? undefined,
    }),
    getPartnerTermsheet: builder.query<
      Termsheet,
      { partner_id: string; account_id?: string } | string
    >({
      query: (arg) => {
        let partner_id: string;
        let account_id: string | undefined;
        if (typeof arg === "string") {
          partner_id = arg;
        } else {
          partner_id = arg.partner_id;
          account_id = arg.account_id;
        }
        let url = `${ACCOUNTS_V2}/${partner_id}/termsheet`;
        if (account_id) {
          url += `?account_id=${account_id}`;
        }
        return {
          url,
          method: "get",
        };
      },
      providesTags: ["TERMSHEETS", "Partners"],
      transformResponse: (response) => response?.data,
    }),
    getTermsheetById: builder.query<
      TermsheetWithPartner,
      { id: string; account_id?: string } | string
    >({
      query: (arg) => {
        let id: string;
        let account_id: string | undefined;
        if (typeof arg === "string") {
          id = arg;
        } else {
          id = arg.id;
          account_id = arg.account_id;
        }
        let url = `${ACCOUNTS_V2}/termsheet/${id}`;
        if (account_id) {
          url += `?account_id=${account_id}`;
        }
        return {
          url,
          method: "get",
        };
      },
      providesTags: ["TERMSHEETS", "Partners"],
      transformResponse: (response) => response?.data,
    }),
    getTermsheetByInternalName: builder.query<
      TermsheetWithPartner,
      { internal_name: string; account_id?: string } | string
    >({
      query: (arg) => {
        let internal_name: string;
        let account_id: string | undefined;
        if (typeof arg === "string") {
          internal_name = arg;
        } else {
          internal_name = arg.internal_name;
          account_id = arg.account_id;
        }
        let url = `${ACCOUNTS_V2}/termsheet/internal/${internal_name}`;
        if (account_id) {
          url += `?account_id=${account_id}`;
        }
        return {
          url,
          method: "get",
        };
      },
      providesTags: ["TERMSHEETS", "Partners"],
      transformResponse: (response) => response?.data,
    }),
    downloadTermsheetPdf: builder.query<
      Blob,
      { id: string; account_id?: string }
    >({
      async queryFn({ id, account_id }) {
        try {
          const token = JSON.parse(
            localStorage.getItem("token") || "{}",
          )?.value;
          let url = `${VITE_API_V2_URL}${ACCOUNTS_V2}/termsheet/${id}/pdf`;
          if (account_id) {
            url += `?account_id=${account_id}`;
          }
          const response = await import("axios").then(({ default: axios }) =>
            axios.get(url, {
              responseType: "blob",
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "application/pdf",
              },
            }),
          );
          return { data: response.data as Blob };
        } catch (error) {
          return { error };
        }
      },
    }),
    getTermsheetPreview: builder.query<
      string,
      { id: string; account_id?: string; show_internal?: boolean }
    >({
      async queryFn({ id, account_id, show_internal }) {
        try {
          const token = JSON.parse(
            localStorage.getItem("token") || "{}",
          )?.value;
          const params = new URLSearchParams();
          if (account_id) {
            params.set("account_id", account_id);
          }
          if (show_internal) {
            params.set("show_internal", "true");
          }
          const query = params.toString();
          const url = `${VITE_API_V2_URL}${ACCOUNTS_V2}/termsheet/${id}/preview${query ? `?${query}` : ""}`;
          const response = await import("axios").then(({ default: axios }) =>
            axios.get(url, {
              responseType: "text",
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/html",
              },
            }),
          );
          return { data: response.data as string };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "TERMSHEETS", id: `${arg.id}-preview` },
      ],
    }),
    updateLeadPartnerTermsheet: builder.mutation<
      Termsheet,
      {
        lead_id: string;
        partner_id: string;
        body: UpdatePartnerTermsheet;
        account_id?: string;
      }
    >({
      query: (payload) => {
        const { lead_id, partner_id, body, account_id } = payload;
        let url = `${ACCOUNTS_V2}/${lead_id}/partners/${partner_id}/termsheet`;
        if (account_id) {
          url += `?account_id=${account_id}`;
        }
        return {
          url,
          method: "patch",
          data: body,
        };
      },
      transformResponse: (response) => response?.data,
      onQueryStarted: async (
        { lead_id, partner_id, body, account_id },
        api,
      ) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            lead_id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
          if (newTermsheet?.id) {
            api.dispatch(
              accountrtk.util.invalidateTags([
                { type: "TERMSHEETS", id: `${newTermsheet.id}-preview` },
              ]),
            );
          }
        } catch (e) {
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    getApproversList: builder.query<TermsheetApprover[], null>({
      query: () => ({
        url: `${ACCOUNTS_V2}/termsheet/approver-list`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getTermsheetComments: builder.query<
      TermsheetComment[],
      {
        partner_id: string;
        account_id: string;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/comments?lead_id=${payload.account_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: (result, _, arg) =>
        result
          ? [
              { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: arg.partner_id },
              ACCOUNT_TAGS.TERMSHEET_COMMENTS,
            ]
          : [ACCOUNT_TAGS.TERMSHEET_COMMENTS],
    }),
    termsheetSubmitForInternalApproval: builder.mutation<
      Termsheet,
      {
        partner_id: string;
        body: InternalApprovalTermSheetSubmitRequest;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/send-for-approval`,
        method: "post",
        data: payload.body,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, err, args) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: args.partner_id },
      ],
      onQueryStarted: async ({ partner_id, body }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    termsheetSubmitRecall: builder.mutation<
      Termsheet,
      {
        partner_id: string;
        body: TermsheetRecallRequest;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/recall`,
        method: "post",
        data: payload.body,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, err, args) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: args.partner_id },
      ],
      onQueryStarted: async ({ partner_id, body }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    generateNewTermsheet: builder.mutation<
      Termsheet,
      { partner_id: string; bank_account_option?: string }
    >({
      query: ({ partner_id, bank_account_option }) => ({
        url: `${ACCOUNTS_V2}/${partner_id}/termsheet/generate`,
        method: "post",
        body: bank_account_option ? { bank_account_option } : undefined,
      }),
      invalidatesTags: (result, err, arg) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: arg.partner_id },
      ],
      transformResponse: (response) => response?.data,
      onQueryStarted: async (arg, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            arg.partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    termsheetInternalApprove: builder.mutation<
      Termsheet,
      {
        partner_id: string;
        body: InternalApproveRequest;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/internal_approve`,
        method: "post",
        data: payload.body,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, err, args) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: args.partner_id },
      ],
      onQueryStarted: async ({ partner_id }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    termsheetInternalReject: builder.mutation<
      Termsheet,
      {
        partner_id: string;
        body: InternalRejectRequest;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/internal_reject`,
        method: "post",
        data: payload.body,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, err, args) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: args.partner_id },
      ],
      onQueryStarted: async ({ partner_id }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    reassignTermsheet: builder.mutation<
      Termsheet,
      {
        partner_id: string;
        body: TermsheetReassignRequest;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/${payload.partner_id}/termsheet/reassign`,
        method: "put",
        data: payload.body,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: (result, err, args) => [
        { type: ACCOUNT_TAGS.TERMSHEET_COMMENTS, id: args.partner_id },
      ],
      onQueryStarted: async ({ partner_id }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    customerApprove: builder.mutation<
      Termsheet,
      { partner_id: string; body: { lead_id: string } }
    >({
      query: ({ partner_id, body }) => ({
        url: `${ACCOUNTS_V2}/${partner_id}/termsheet/customer_approve`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => response?.data,
      onQueryStarted: async ({ partner_id }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          console.log(e);
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),
    customerReject: builder.mutation<
      Termsheet,
      { partner_id: string; body: CustomerRejectRequest }
    >({
      query: ({ partner_id, body }) => ({
        url: `${ACCOUNTS_V2}/${partner_id}/termsheet/customer_reject`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => response?.data,
      onQueryStarted: async ({ partner_id, body }, api) => {
        try {
          const { data: newTermsheet } = await api.queryFulfilled;
          updateTermsheet(
            partner_id,
            newTermsheet.lead.id,
            newTermsheet,
            api.dispatch,
            accountrtk,
          );
        } catch (e) {
          api.dispatch(accountrtk.util.invalidateTags(["TERMSHEETS"]));
        }
      },
    }),

    exportLeadToSalesforce: builder.mutation<any, string>({
      query: (lead_id) => {
        return {
          url: `/origination/leads/${lead_id}/sf-push`,
          method: "POST",
        };
      },
      invalidatesTags: ["LEAD"],
    }),
    addShareholder: builder.mutation<
      any,
      { account_id: string; data: AddShareholderData }
    >({
      query: ({ account_id, data }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/shareholders`,
        method: "POST",
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error adding shareholder:", error);
        }
      },
    }),
    deleteShareholder: builder.mutation<
      any,
      { account_id: string; shareholder_id: string }
    >({
      query: ({ account_id, shareholder_id }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/shareholders/${shareholder_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error deleting shareholder:", error);
        }
      },
    }),
    editShareholder: builder.mutation<
      any,
      { account_id: string; shareholder_id: string; data: EditShareholderData }
    >({
      query: ({ account_id, shareholder_id, data }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/shareholders/${shareholder_id}`,
        method: "PUT",
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error editing shareholder:", error);
        }
      },
    }),
    getDirectors: builder.query<any[], string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors`,
        method: "get",
      }),
      providesTags: ["SHAREHOLDERS", "ADDRESSPROOF"],
      transformResponse: (response) => response?.data,
    }),
    getDirectorDocuments: builder.query<
      any[],
      { account_id: string; director_id: string }
    >({
      query: ({ account_id, director_id }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors/${director_id}/documents`,
        method: "get",
      }),
      providesTags: ["SHAREHOLDERS", "ADDRESSPROOF"],
      transformResponse: (response) => response?.data,
    }),
    getDirectorRequiredDocuments: builder.query<any[], string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors/required-documents`,
        method: "get",
      }),
      providesTags: ["SHAREHOLDERS", "ADDRESSPROOF"],
      transformResponse: (response) => response?.data,
    }),
    addDirector: builder.mutation<
      any,
      { account_id: string; data: AddDirectorData }
    >({
      query: ({ account_id, data }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors`,
        method: "POST",
        data,
      }),
      transformResponse: (response) => response?.data,
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error adding director:", error);
        }
      },
    }),
    addDocumentToDirector: builder.mutation<
      any,
      {
        account_id: string;
        director_id: string;
        data: AddDocumentToDirectorData;
      }
    >({
      query: ({ account_id, director_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_type", data.document_type);
        formData.append("document_category", data.document_category);
        return {
          url: `${ACCOUNTS_V2}/${account_id}/directors/${director_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["ADDRESSPROOF"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(accountrtk.util.invalidateTags(["ADDRESSPROOF"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error adding ID:", error);
        }
      },
    }),
    editDirector: builder.mutation<
      any,
      { account_id: string; director_id: string; data: EditDirectorData }
    >({
      query: ({ account_id, director_id, data }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors/${director_id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error editing director:", error);
        }
      },
    }),
    deleteDirector: builder.mutation<
      any,
      { account_id: string; director_id: string }
    >({
      query: ({ account_id, director_id }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/directors/${director_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SHAREHOLDERS"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(maps.util.invalidateTags(["SHAREHOLDERS"]));
          dispatch(compliance.util.invalidateTags(["SHAREHOLDERS"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error deleting director:", error);
        }
      },
    }),

    addDocumentToContact: builder.mutation<
      any,
      { account_id: string; contact_id: string; data: AddDocumentToContactData }
    >({
      query: ({ account_id, contact_id, data }) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("document_type", data.document_type);
        formData.append("document_category", data.document_category);
        return {
          url: `${ACCOUNTS_V2}/${account_id}/contacts/${contact_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      transformResponse: (response) => response?.data,
      invalidatesTags: ["IDVerification"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(accountrtk.util.invalidateTags(["IDVerification"]));
          dispatch(users_v2.util.invalidateTags(["IDVerification"]));
        } catch (error) {
          //eslint-disable-next-line no-console
          console.error("Error adding ID:", error);
        }
      },
    }),

    getContactDocuments: builder.query<
      CompanyDoc[],
      { contact_id: string; account_id: string }
    >({
      query: ({ contact_id, account_id }) => ({
        url: `${ACCOUNTS_V2}/${account_id}/contacts/${contact_id}/documents`,
        method: "get",
      }),
      providesTags: ["IDVerification"],
      transformResponse: (response) => response?.data,
    }),
    getContactRequiredDocuments: builder.query<any[], string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/contacts/required-documents`,
        method: "get",
      }),
      providesTags: ["IDVerification"],
      transformResponse: (response) => response?.data,
    }),
    getAccountOnboardingStatus: builder.query<AccountOnboardingStatus, string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/onboarding-status`,
        method: "get",
      }),
      providesTags: (_, __, arg) => [
        "ACCOUNT_ONBOARDING_STATUS",
        { type: "ACCOUNT_ONBOARDING_STATUS", id: arg },
      ],
      transformResponse: (response) => response?.data,
    }),
    getProduct: builder.query<any, string>({
      query: (account_id) => ({
        url: `${ACCOUNTS_V2}/${account_id}/relationships/products`,
        method: "get",
      }),
    }),
    getSFLead: builder.query<{ data: ISFLead }, string>({
      query: (lead_sf_id) => ({
        url: `${ACCOUNTS_V2}/leads/sf/${lead_sf_id}`,
        method: "get",
        transformResponse: (response: { data: ISFLead }) => response?.data,
      }),
    }),
    importSFLead: builder.mutation<{ task_id: string }, string>({
      query: (lead_sf_id) => ({
        url: `${ACCOUNTS_V2}/leads/sf/${lead_sf_id}/import`,
        method: "post",
      }),
      transformResponse: (response) => response?.data,
    }),
    getSFLeadImportProgress: builder.query<
      { progress: number; status: "IN_PROGRESS" | "FAILED" | "COMPLETED" },
      string
    >({
      query: (task_id) => ({
        url: `${ACCOUNTS_V2}/leads/sf/import/${task_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getTermsheetBillingList: builder.query<any, { billingparams: string }>({
      query: ({ billingparams }) => ({
        url: `${ACCOUNTS_V2}/termsheet/billing-list${billingparams}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),

    getCustomerTermsheetBillingList: builder.query<TermsheetInvoice[], null>({
      query: () => ({
        url: `${ACCOUNTS_V2}/termsheet/customer-billing-list`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getTermsheetBill: builder.query<TermsheetInvoice, string>({
      query: (internalName) => ({
        url: `${ACCOUNTS_V2}/termsheet/billing-detail/${internalName}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getTermsheetBillById: builder.query<TermsheetInvoice, string>({
      query: (id) => ({
        url: `${ACCOUNTS_V2}/termsheet/customer-billing-detail/${id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    markInvoiceAsPaid: builder.mutation<TermsheetInvoice, string>({
      query: (id) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${id}/paid`,
        method: "post",
      }),
      transformResponse: (response) => response?.data,
    }),
    getInvoiceRequiredDocuemnts: builder.query<RequiredDoc[], string>({
      query: (invoice_id) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${invoice_id}/required-documents`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
    }),
    uploadInvoiceProofOfpayment: builder.mutation<
      CompanyDoc,
      { invoiceId: string; body: AddDocumentToBillingInvoice }
    >({
      query: (payload) => {
        const formData = new FormData();
        formData.append("file", payload.body.file);
        formData.append("document_type", payload.body.document_type);
        formData.append("document_category", payload.body.document_category);
        return {
          url: `${ACCOUNTS_V2}/termsheet/billings/${payload.invoiceId}/documents`,
          method: "post",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      invalidatesTags: ["BILLING_DOCS"],
      transformResponse: (response) => response.data,
    }),
    getInvoiceProofOfPaymentDocs: builder.query<CompanyDoc[], string>({
      query: (invoice_id) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${invoice_id}/documents`,
        method: "get",
      }),
      providesTags: ["BILLING_DOCS"],
      transformResponse: (response) => response.data,
    }),
    getInvoiceProofOfPaymentDocumentUrl: builder.query<
      CompanyDoc,
      {
        invoice_id: string;
        document_id: string;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${payload.invoice_id}/documents/${payload.document_id}`,
        method: "get",
      }),
      providesTags: ["BILLING_DOCS"],
      transformResponse: (response) => response.data,
    }),
    downloadProofOfPaymentDocument: builder.query<
      string,
      {
        invoice_id: string;
        document_id: string;
      }
    >({
      query: (payload) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${payload.invoice_id}/documents/${payload.document_id}/download`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
    }),
    // Added: Delete Invoice Proof of Payment Document
    deleteInvoiceProofOfPaymentDoc: builder.mutation<
      void,
      { billing_id: string; document_id: string }
    >({
      query: ({ billing_id, document_id }) => ({
        url: `${ACCOUNTS_V2}/termsheet/billings/${billing_id}/documents/${document_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BILLING_DOCS"],
    }),
    createStripeCheckout: builder.mutation<
      {
        checkout_url: string;
        session_id: string;
        currency: string;
        amount_charged: number;
      },
      string
    >({
      query: (internal_name: string) => ({
        url: `${ACCOUNTS_V2}/termsheet/billing-payment/invoice/${internal_name}`,
        method: "POST",
      }),
      transformResponse: (response: any) => response?.data,
    }),
    verifyStripeSession: builder.query<
      { status: "success" | "failed" },
      string
    >({
      query: (session_id) => ({
        url: `${ACCOUNTS_V2}/termsheet/stripe/verify-session/${session_id}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.data,
    }),
    uploadFinancialReport: builder.mutation<
      void,
      { accountId: string; file: File }
    >({
      query: ({ accountId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `${ACCOUNTS_V2}/${accountId}/financial-reports/upload`,
          method: "POST",
          data: formData,
        };
      },
      invalidatesTags: (result, error, { accountId }) => {
        if (error) {
          return [];
        }
        return [{ type: "ACCOUNT_DOC_UPLOADS", id: accountId }];
      },
    }),
  }),
});

export const {
  useGetAccountRelationshipQuery,
  useUpdateSubmitStatusMutation,
  useSubmitAccountMutation,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useGetBuyersQuery,
  useGetBuyerCompaniesQuery,
  useGetPartnerContactsQuery,
  useGetGoodsQuery,
  useGetAccountingSoftwaresQuery,
  useInviteTradePartnersMutation,
  useUpdateTermSheetStatusMutation,
  useVendorAddressMutation,
  useGetDocumentsQuery,
  useGetDocumentContentQuery,
  useAssociateDocsMutation,
  useGetUserDocsQuery,
  useDeleteDocMutation,
  useFetchMemberDocumentsQuery,
  useFetchMemberPendingDocumentsQuery,
  useGetLeadsQuery,
  useToggleLeadActiveStatusMutation,
  useGetLeadByURIQuery,
  useAddAccountProfileMutation,
  useUpdateStepsMutation,
  useGetPartnersQuery,
  useGetBuyerCreditLimitQuery,
  useGetDocumentUrlQuery,
  useLazyGetDocumentUrlQuery,
  useGetAccountDocumentsQuery,
  useModifyAccountRelationshipMutation,
  usePatchAccountRelationshipMutation,
  useGetLeadByNameQuery,
  useGetLeadSfExportReadinessQuery,
  useGetLeadAccountTermsheetInfoQuery,
  useUpdateTermsheetInfoMutation,
  useGetLeadPartnerTermsheetQuery,
  useUpdateLeadPartnerTermsheetMutation,
  useTermsheetSubmitForInternalApprovalMutation,
  useTermsheetSubmitRecallMutation,
  useGenerateNewTermsheetMutation,
  useTermsheetInternalApproveMutation,
  useTermsheetInternalRejectMutation,
  useReassignTermsheetMutation,
  useGetAccountQuery,
  useGetAccountsQuery,
  useGetAccountByNameQuery,
  useDeleteShareholderMutation,
  useAddShareholderMutation,
  useGetDirectorsQuery,
  useGetDirectorDocumentsQuery,
  useGetDirectorRequiredDocumentsQuery,
  useAddDirectorMutation,
  useAddDocumentToDirectorMutation,
  useDeleteDirectorMutation,
  useEditDirectorMutation,
  useEditShareholderMutation,
  useAddDocumentToContactMutation,
  useGetContactRequiredDocumentsQuery,
  useGetContactDocumentsQuery,
  useGetAccountOnboardingStatusQuery,
  useLazyFetchMemberDocumentsQuery,
  useGetProductQuery,
  useGetAccountSoftwareQuery,
  useExportLeadToSalesforceMutation,
  useLazyGetAccountOnboardingStatusQuery,
  useGetSFLeadQuery,
  useImportSFLeadMutation,
  useGetSFLeadImportProgressQuery,
  useGetTermsheetListQuery,
  useGetTermsheetStatusCountsQuery,
  useGetCustomerTemsheetListQuery,
  useGetApproversListQuery,
  useGetTermsheetCommentsQuery,
  useGetPartnerTermsheetQuery,
  useGetTermsheetByInternalNameQuery,
  useGetTermsheetByIdQuery,
  useLazyDownloadTermsheetPdfQuery,
  useGetTermsheetPreviewQuery,
  useCustomerApproveMutation,
  useCustomerRejectMutation,
  useGetDashboardTermsheetListQuery,
  useGetTermsheetBillingListQuery,
  useGetCustomerTermsheetBillingListQuery,
  useGetTermsheetBillQuery,
  useGetTermsheetBillByIdQuery,
  useUpdateTermsheetCountdownMutation,
  useMarkInvoiceAsPaidMutation,
  useUploadInvoiceProofOfpaymentMutation,
  useGetInvoiceProofOfPaymentDocsQuery,
  useGetInvoiceProofOfPaymentDocumentUrlQuery,
  useGetInvoiceRequiredDocuemntsQuery,
  useDeleteInvoiceProofOfPaymentDocMutation,
  useCreateStripeCheckoutMutation,
  useLazyVerifyStripeSessionQuery,
  useUploadFinancialReportMutation,
} = accountrtk;

export { useGetAccountByIdQuery } from "hooks/useGetAccountById";
